import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { message, Spin, Icon } from 'antd'
import { AuditProductAPI } from '@getgreenline/homi-shared'
import { AuditStore, ICachedAuditProductContract } from '../../EditAuditStore'
import { CurrentCompanyStore } from '../../../../../../../stores/CurrentCompanyStore'
import { parseErrorMsg, removeDecimalPoint } from '../../../../../../../utilities/helpers'
import { AuditSyncIndicator } from '../AuditSyncIndicator'
import { AuditProductModels } from '@getgreenline/audits'
import { isNil } from 'lodash'
import { AntAuditInputNumber } from '../AntAuditInputNumber'

interface Props {
  product: AuditProductModels.EditAuditProduct
  auditStore: AuditStore
}

interface State {
  isUpdating: boolean
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

@inject('currentCompanyStore')
@observer
export class AuditProductEnteredQuantityInput extends React.Component<
  Props & InjectedProps,
  State
> {
  company = this.props.currentCompanyStore!.company!

  MESSAGE_KEY = 'QUANTITY_SYNC'

  constructor(props: Props) {
    super(props)

    this.state = {
      isUpdating: false,
    }
  }

  locallySaveEnteredQuantity = (value: number | string | undefined) => {
    const { product, auditStore } = this.props

    const quantity = isNil(value) || value === '' ? null : value

    if (typeof quantity !== 'string') {
      product.locallySaveEnteredQuantity(quantity)

      const existingCache = auditStore.getAuditProductSyncCache('enteredQuantity', product.id)

      const syncAttempted = existingCache?.syncAttempted || false

      auditStore.setAuditProductEQSyncCache({
        syncAttempted,
        syncSuccessful: undefined,
        cachedAuditProductContract: {
          id: product.id,
          inventoryAuditId: auditStore.audit.id,
          productId: product.productId,
          expectedQuantity: product.expectedQuantity,
          enteredQuantity: quantity,
        },
      })
    }
  }

  syncEnteredQuantityOnBlur = async () => {
    const { product, auditStore } = this.props

    const auditProductCache = auditStore.getAuditProductSyncCache('enteredQuantity', product.id)

    if (!auditProductCache) {
      return
    }

    if (product.enteredQuantity === auditProductCache.cachedAuditProductContract.enteredQuantity) {
      return
    }

    await this.syncEnteredQuantity(auditProductCache.cachedAuditProductContract)
  }

  syncEnteredQuantity = async (
    auditProductContract: ICachedAuditProductContract<'enteredQuantity'>,
  ) => {
    const { product, auditStore } = this.props

    this.setState({ isUpdating: true })

    try {
      const patchedAuditProduct = await AuditProductAPI.patchAuditProduct(this.company.id, {
        ...auditProductContract,
        notes: undefined,
      })

      product.setEnteredQuantity(patchedAuditProduct.enteredQuantity)

      auditStore.setAuditProductEQSyncCache({
        syncAttempted: true,
        syncSuccessful: true,
        cachedAuditProductContract: patchedAuditProduct,
      })
    } catch (error) {
      auditStore.setAuditProductEQSyncCache({
        syncAttempted: true,
        syncSuccessful: false,
        cachedAuditProductContract: auditProductContract,
      })

      message.error({
        content:
          parseErrorMsg(error) ||
          `An error occurred while updating ${product.productName}'s quantity`,
        key: this.MESSAGE_KEY,
        duration: 15,
      })
    } finally {
      this.setState({ isUpdating: false })
    }
  }

  render() {
    const { product, auditStore } = this.props
    const { isUpdating } = this.state
    const auditProductQuantityCache = auditStore.getAuditProductSyncCache(
      'enteredQuantity',
      product.id,
    )

    return (
      <div className='d-flex align-items-center'>
        <AntAuditInputNumber
          className='w-100'
          isEditable={this.props.auditStore.isEditable}
          value={this.props.product.locallySavedEnteredQuantity}
          min={0}
          onChange={this.locallySaveEnteredQuantity}
          onBlur={this.syncEnteredQuantityOnBlur}
          disabled={isUpdating}
          formatter={(value) => {
            // Formatter function expects only string values to be returned
            if (value === '' || isNil(value)) {
              return ''
            }
            return `${isNaN(removeDecimalPoint(value)) ? '' : removeDecimalPoint(value)}`
          }}
        />

        <div
          style={{
            minWidth: 21,
            textAlign: 'right',
          }}
        >
          {isUpdating ? (
            <Spin indicator={<Icon type='loading' spin />} size='small' />
          ) : (
            auditProductQuantityCache !== undefined &&
            auditStore.audit.status === 'pending' && (
              <AuditSyncIndicator
                valueChanged={product.locallySavedEnteredQuantity !== product.enteredQuantity}
                syncAttempted={auditProductQuantityCache.syncAttempted}
                syncSuccessful={auditProductQuantityCache.syncSuccessful}
                onClick={async () =>
                  this.syncEnteredQuantity(auditProductQuantityCache.cachedAuditProductContract)
                }
              />
            )
          )}
        </div>
      </div>
    )
  }
}
